/**
 * MarcDiffDisplay — shows field-level diff between proposal MARC data and
 * the original published record's MARC data.
 *
 * Color coding:
 * - Green: field present only in proposal (added)
 * - Red: field present only in original (removed)
 * - Amber/yellow: field present in both but with different subfield values (changed)
 * - Normal: fields identical in both
 *
 * Related UCs: AR-05a — diff display for UPDATE_EXISTING proposals
 */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Accordion } from '@folio/stripes/components';
/** Build a lookup key for a field: fieldTag:fieldIndex. */
var fieldKey = function (f) { return "".concat(f.fieldTag, ":").concat(f.fieldIndex); };
/** Compare two subfield arrays. Returns true if they have identical content. */
var subfieldsEqual = function (a, b) {
    if (a.length !== b.length)
        return false;
    for (var i = 0; i < a.length; i++) {
        if (a[i].subfieldCode !== b[i].subfieldCode)
            return false;
        if (a[i].value !== b[i].value)
            return false;
        if (a[i].subfieldIndex !== b[i].subfieldIndex)
            return false;
    }
    return true;
};
/** Compare indicators between two fields. */
var indicatorsEqual = function (a, b) {
    return a.indicator1 === b.indicator1 && a.indicator2 === b.indicator2;
};
/** Compute diff entries from proposal and original fields. */
var computeDiff = function (proposalFields, originalFields) {
    var originalMap = new Map();
    for (var _i = 0, originalFields_1 = originalFields; _i < originalFields_1.length; _i++) {
        var f = originalFields_1[_i];
        originalMap.set(fieldKey(f), f);
    }
    var proposalMap = new Map();
    for (var _a = 0, proposalFields_1 = proposalFields; _a < proposalFields_1.length; _a++) {
        var f = proposalFields_1[_a];
        proposalMap.set(fieldKey(f), f);
    }
    var entries = [];
    var processedKeys = new Set();
    // Process proposal fields first (preserves proposal order)
    for (var _b = 0, proposalFields_2 = proposalFields; _b < proposalFields_2.length; _b++) {
        var pf = proposalFields_2[_b];
        var key = fieldKey(pf);
        processedKeys.add(key);
        var original = originalMap.get(key);
        if (!original) {
            entries.push({ field: pf, status: 'added' });
        }
        else if (!indicatorsEqual(pf, original) || !subfieldsEqual(pf.subfields, original.subfields)) {
            entries.push({ field: pf, original: original, status: 'changed' });
        }
        else {
            entries.push({ field: pf, status: 'unchanged' });
        }
    }
    // Find removed fields (in original but not in proposal)
    for (var _c = 0, originalFields_2 = originalFields; _c < originalFields_2.length; _c++) {
        var origField = originalFields_2[_c];
        var key = fieldKey(origField);
        if (!processedKeys.has(key)) {
            entries.push({ field: origField, status: 'removed' });
        }
    }
    return entries;
};
var MONO_FONT = "'SF Mono', 'Cascadia Code', 'Consolas', monospace";
/** Background colors for diff statuses. */
var DIFF_COLORS = {
    added: { bg: '#f0fdf4', border: '#86efac', indicator: '#16a34a' },
    removed: { bg: '#fef2f2', border: '#fca5a5', indicator: '#dc2626' },
    changed: { bg: '#fffbeb', border: '#fcd34d', indicator: '#d97706' },
    unchanged: { bg: 'transparent', border: 'transparent', indicator: 'transparent' },
};
var TAG_BADGE_WIDTH = '72px';
var isControlField = function (tag) { return tag < '010'; };
var formatIndicator = function (ind) {
    return !ind || ind.trim() === '' ? '#' : ind;
};
/** Build subfield-level diff parts for a `changed` field. */
var computeSubfieldParts = function (proposalSubfields, originalSubfields) {
    var origKey = function (s) { var _a, _b; return "".concat((_a = s.subfieldCode) !== null && _a !== void 0 ? _a : '', ":").concat((_b = s.subfieldIndex) !== null && _b !== void 0 ? _b : 0); };
    var originalMap = new Map();
    for (var _i = 0, originalSubfields_1 = originalSubfields; _i < originalSubfields_1.length; _i++) {
        var s = originalSubfields_1[_i];
        originalMap.set(origKey(s), s);
    }
    var parts = [];
    var seen = new Set();
    for (var _a = 0, proposalSubfields_1 = proposalSubfields; _a < proposalSubfields_1.length; _a++) {
        var ps = proposalSubfields_1[_a];
        var key = origKey(ps);
        seen.add(key);
        var os = originalMap.get(key);
        if (!os) {
            parts.push({ status: 'added', subfieldCode: ps.subfieldCode, value: ps.value });
        }
        else if (os.value !== ps.value) {
            parts.push({ status: 'changed', subfieldCode: ps.subfieldCode, value: ps.value, originalValue: os.value });
        }
        else {
            parts.push({ status: 'unchanged', subfieldCode: ps.subfieldCode, value: ps.value });
        }
    }
    for (var _b = 0, originalSubfields_2 = originalSubfields; _b < originalSubfields_2.length; _b++) {
        var os = originalSubfields_2[_b];
        if (!seen.has(origKey(os))) {
            parts.push({ status: 'removed', subfieldCode: os.subfieldCode, value: os.value });
        }
    }
    return parts;
};
/** A single subfield chip with per-status styling, optionally showing old → new for `changed`. */
var SubfieldChip = function (_a) {
    var part = _a.part, control = _a.control;
    var addedStyle = DIFF_COLORS.added;
    var removedStyle = DIFF_COLORS.removed;
    var renderCodeBadge = function (bg) { return (!control && (React.createElement("span", { style: {
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4.5px 5px',
            fontFamily: MONO_FONT,
            fontWeight: 700,
            fontSize: '0.75rem',
            color: '#fff',
            backgroundColor: bg,
            opacity: 0.85,
            borderRadius: '5px 0 0 5px',
        } }, part.subfieldCode))); };
    var valueBoxStyle = function (bg, border, color, strike) { return ({
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4.5px 8px',
        fontFamily: MONO_FONT,
        fontSize: '0.8rem',
        color: color,
        backgroundColor: bg,
        border: "1px solid ".concat(border),
        borderLeft: control ? undefined : 'none',
        borderRadius: control ? '5px' : '0 5px 5px 0',
        whiteSpace: 'nowrap',
        textDecoration: strike ? 'line-through' : 'none',
    }); };
    if (part.status === 'changed') {
        // Show old value (strikethrough, red bg) + arrow + new value (green bg)
        return (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'stretch', gap: '4px', flexShrink: 0 } },
            React.createElement("span", { style: { display: 'inline-flex', alignItems: 'stretch', flexShrink: 0 } },
                renderCodeBadge('#9ca3af'),
                React.createElement("span", { style: valueBoxStyle(removedStyle.bg, removedStyle.border, '#6b7280', true) }, part.originalValue)),
            React.createElement("span", { style: { alignSelf: 'center', fontSize: '0.85rem', color: '#64748b' } }, "\u2192"),
            React.createElement("span", { style: { display: 'inline-flex', alignItems: 'stretch', flexShrink: 0 } },
                renderCodeBadge('#475569'),
                React.createElement("span", { style: valueBoxStyle(addedStyle.bg, addedStyle.border, '#334155', false) }, part.value))));
    }
    var bg = part.status === 'added' ? addedStyle.bg
        : part.status === 'removed' ? removedStyle.bg
            : '#f8fafc';
    var border = part.status === 'added' ? addedStyle.border
        : part.status === 'removed' ? removedStyle.border
            : '#cbd5e1';
    var codeBg = part.status === 'removed' ? '#9ca3af' : '#475569';
    var color = part.status === 'removed' ? '#6b7280' : '#334155';
    return (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'stretch', borderRadius: '5px', flexShrink: 0 } },
        renderCodeBadge(codeBg),
        React.createElement("span", { style: valueBoxStyle(bg, border, color, part.status === 'removed') }, part.value)));
};
/**
 * Renders a single diff row.
 */
var DiffFieldRow = function (_a) {
    var entry = _a.entry;
    var field = entry.field, status = entry.status, original = entry.original;
    var colors = DIFF_COLORS[status];
    var control = isControlField(field.fieldTag);
    var tagLabel = control
        ? field.fieldTag
        : "".concat(field.fieldTag, " ").concat(formatIndicator(field.indicator1)).concat(formatIndicator(field.indicator2));
    var subfieldParts = status === 'changed' && original
        ? computeSubfieldParts(field.subfields, original.subfields)
        : null;
    return (React.createElement("div", { "data-testid": "marc-diff-field-".concat(field.fieldTag, "-").concat(status), style: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '4px',
            margin: '4px 0',
            padding: '4px 8px',
            backgroundColor: colors.bg,
            borderLeft: "3px solid ".concat(colors.indicator),
            borderRadius: '4px',
        } },
        status !== 'unchanged' && (React.createElement("span", { style: {
                display: 'inline-flex',
                alignItems: 'center',
                padding: '4px 6px',
                fontSize: '0.7rem',
                fontWeight: 700,
                color: colors.indicator,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                flexShrink: 0,
                minWidth: '58px',
            } },
            status === 'added' && '+',
            status === 'removed' && '\u2212',
            status === 'changed' && '\u0394')),
        status === 'unchanged' && React.createElement("span", { style: { minWidth: '58px', flexShrink: 0 } }),
        React.createElement("span", { style: {
                display: 'inline-flex',
                alignItems: 'center',
                padding: '5px 7px',
                fontFamily: MONO_FONT,
                fontWeight: 600,
                fontSize: '0.8rem',
                color: '#fff',
                backgroundColor: status === 'removed' ? '#9ca3af' : '#475569',
                borderRadius: '5px',
                whiteSpace: 'nowrap',
                letterSpacing: '0.02em',
                flexShrink: 0,
                width: TAG_BADGE_WIDTH,
                justifyContent: 'center',
                textDecoration: status === 'removed' ? 'line-through' : 'none',
                opacity: status === 'removed' ? 0.7 : 1,
            } }, tagLabel),
        React.createElement("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1, minWidth: 0 } }, subfieldParts
            ? subfieldParts.map(function (part, idx) { return (React.createElement(SubfieldChip, { key: "sf-".concat(field.fieldTag, "-").concat(field.fieldIndex, "-").concat(idx, "-").concat(part.status), part: part, control: control })); })
            : field.subfields.map(function (sf) {
                var key = "".concat(field.fieldTag, "-").concat(field.fieldIndex, "-").concat(sf.subfieldCode, "-").concat(sf.subfieldIndex);
                return (React.createElement("span", { key: key, style: { display: 'inline-flex', alignItems: 'stretch', borderRadius: '5px', flexShrink: 0 } },
                    !control && (React.createElement("span", { style: {
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4.5px 5px',
                            fontFamily: MONO_FONT,
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            color: '#fff',
                            backgroundColor: status === 'removed' ? '#9ca3af' : '#475569',
                            opacity: 0.85,
                            borderRadius: '5px 0 0 5px',
                        } }, sf.subfieldCode)),
                    React.createElement("span", { style: {
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4.5px 8px',
                            fontFamily: MONO_FONT,
                            fontSize: '0.8rem',
                            color: status === 'removed' ? '#6b7280' : '#334155',
                            backgroundColor: status === 'unchanged' ? '#f8fafc' : colors.bg,
                            border: "1px solid ".concat(status === 'unchanged' ? '#cbd5e1' : colors.border),
                            borderLeft: control ? undefined : 'none',
                            borderRadius: control ? '5px' : '0 5px 5px 0',
                            whiteSpace: 'nowrap',
                            textDecoration: status === 'removed' ? 'line-through' : 'none',
                        } }, sf.value)));
            }))));
};
/**
 * MarcDiffDisplay — renders a visual diff between proposal and original MARC data.
 */
var MarcDiffDisplay = function (_a) {
    var proposalFields = _a.proposalFields, originalFields = _a.originalFields;
    if (proposalFields.length === 0 && originalFields.length === 0) {
        return null;
    }
    var diffEntries = computeDiff(proposalFields, originalFields);
    var legend = (React.createElement("div", { style: { display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '0.8rem' } },
        React.createElement("span", { style: { display: 'flex', alignItems: 'center', gap: '4px' } },
            React.createElement("span", { style: { width: '12px', height: '12px', backgroundColor: DIFF_COLORS.added.indicator, borderRadius: '2px', display: 'inline-block' } }),
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.diff.added" })),
        React.createElement("span", { style: { display: 'flex', alignItems: 'center', gap: '4px' } },
            React.createElement("span", { style: { width: '12px', height: '12px', backgroundColor: DIFF_COLORS.removed.indicator, borderRadius: '2px', display: 'inline-block' } }),
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.diff.removed" })),
        React.createElement("span", { style: { display: 'flex', alignItems: 'center', gap: '4px' } },
            React.createElement("span", { style: { width: '12px', height: '12px', backgroundColor: DIFF_COLORS.changed.indicator, borderRadius: '2px', display: 'inline-block' } }),
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.diff.changed" }))));
    return (React.createElement(Accordion, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.diff.title" }) },
        legend,
        React.createElement("div", { style: { padding: '0.5rem 0' }, "data-testid": "marc-diff-display" }, diffEntries.map(function (entry) { return (React.createElement(DiffFieldRow, { key: "".concat(entry.field.fieldTag, "-").concat(entry.field.fieldIndex, "-").concat(entry.status), entry: entry })); }))));
};
export default MarcDiffDisplay;
